import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, ArrowRight, Phone, Bot, Shield, DollarSign, Globe, Users } from 'lucide-react';
import SiteFooter from './SiteFooter';
import SEOHead from './SEOHead';

const FEATURE_KEYS = [
  { key: 'callTracking', us: true, them: false },
  { key: 'dpni', us: true, them: false },
  { key: 'callRecording', us: true, them: false },
  { key: 'citationBuilder', us: true, them: true },
  { key: 'napAudits', us: true, them: true },
  { key: 'autoCitations', us: true, them: true },
  { key: 'rankTracking', us: true, them: true },
  { key: 'gbpAudit', us: true, them: true },
  { key: 'seoPages', us: true, them: false },
  { key: 'aiBlog', us: true, them: false },
  { key: 'schemaStacking', us: true, them: false },
  { key: 'reviewMonitoring', us: true, them: true },
  { key: 'reviewGeneration', us: true, them: true },
  { key: 'whiteLabel', us: true, them: true },
  { key: 'impersonation', us: true, them: false },
  { key: 'dunning', us: true, them: false },
  { key: 'failedPayment', us: true, them: false },
  { key: 'sms', us: true, them: false },
  { key: 'email', us: true, them: false },
  { key: 'billing', us: true, them: false },
  { key: 'indexNow', us: true, them: false },
  { key: 'leadAttribution', us: true, them: false },
];

const PRICING = [
  { label: 'BrightLocal Track', price: '$39/mo', features: '7 locations, SEO tools only', per: 'per location after 7' },
  { label: 'BrightLocal Manage', price: '$49/mo', features: '+ Listings Management', per: 'per location after 7' },
  { label: 'BrightLocal Grow', price: '$59/mo', features: '+ Review Management', per: 'per location after 7' },
];

const COLORS = { primary: '#002FA7', dark: '#0a0f1e', accent: '#FFCC00' };
const REASON_ICONS = [Phone, Bot, Shield, DollarSign, Globe, Users];
const REASON_KEYS = ['callTracking', 'aiVisibility', 'dunning', 'sms', 'seoPages', 'impersonation'];

export default function ComparisonBrightLocal() {
  const { t } = useTranslation('compare');
  const usWins = FEATURE_KEYS.filter(f => f.us && !f.them).length;
  const theyWin = FEATURE_KEYS.filter(f => !f.us && f.them).length;
  const both = FEATURE_KEYS.filter(f => f.us && f.them).length;

  return (
    <>
      <SEOHead
        title="LocalBranding Pro vs BrightLocal — Full Feature Comparison 2026"
        description="Compare LocalBranding Pro and BrightLocal side-by-side."
        canonical="https://localbrandingpro.com/compare/brightlocal"
      />
      <div style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
        {/* Hero */}
        <section className="relative overflow-hidden" style={{ background: COLORS.dark }}>
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 70% 30%, ${COLORS.primary}, transparent 60%)` }} />
          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-1.5 rounded-full" style={{ background: `${COLORS.accent}20`, color: COLORS.accent }}>{t('brightlocal.badge')}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="comparison-title">
              {t('brightlocal.title')}
              <span className="block text-2xl sm:text-3xl font-normal text-slate-400 mt-2">{t('brightlocal.titleSub')}</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">{t('brightlocal.subtitle')}</p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold" style={{ color: COLORS.accent }}>{usWins}</p>
                <p className="text-xs text-slate-400 mt-1">{t('brightlocal.onlyWeHave')}</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-white">{both}</p>
                <p className="text-xs text-slate-400 mt-1">{t('brightlocal.sharedFeatures')}</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-slate-500">{theyWin}</p>
                <p className="text-xs text-slate-400 mt-1">{t('brightlocal.theirOnly')}</p>
              </div>
            </div>
            <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.primary }} data-testid="cta-hero">
              {t('brightlocal.cta')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        {/* Feature Table */}
        <section className="py-16 bg-white" data-testid="comparison-table-section">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: COLORS.dark }}>{t('brightlocal.tableTitle')}</h2>
            <p className="text-center text-slate-500 text-sm mb-10 max-w-xl mx-auto">{t('brightlocal.tableSub')}</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-6 py-4 font-semibold text-slate-700">{t('brightlocal.feature')}</th>
                    <th className="text-center px-6 py-4 font-bold" style={{ color: COLORS.primary }}>LocalBranding Pro</th>
                    <th className="text-center px-6 py-4 font-semibold text-slate-500">BrightLocal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {FEATURE_KEYS.map((f, i) => (
                    <tr key={i} className={`${f.us && !f.them ? 'bg-blue-50/30' : ''} hover:bg-slate-50 transition-colors`}>
                      <td className="px-6 py-3.5 text-slate-700">{t(`features.${f.key}`)}</td>
                      <td className="px-6 py-3.5 text-center">{f.us ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                      <td className="px-6 py-3.5 text-center">{f.them ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Why Switch */}
        <section className="py-16" style={{ background: '#f8fafc' }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('brightlocal.whySwitch')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {REASON_KEYS.map((key, i) => {
                const Icon = REASON_ICONS[i];
                return (
                  <div key={key} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                    <Icon className="h-8 w-8 mb-4" style={{ color: COLORS.primary }} />
                    <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.dark }}>{t(`brightlocal.reasons.${key}.title`)}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{t(`brightlocal.reasons.${key}.desc`)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('brightlocal.pricingTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {PRICING.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-6 text-center">
                  <p className="text-sm font-semibold text-slate-500 mb-2">{p.label}</p>
                  <p className="text-3xl font-bold mb-1" style={{ color: COLORS.dark }}>{p.price}</p>
                  <p className="text-xs text-slate-400 mb-3">{p.features}</p>
                  <p className="text-xs text-slate-300">{p.per}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-8 text-center" style={{ background: COLORS.primary }}>
              <p className="text-white/80 text-sm mb-2">LocalBranding Pro</p>
              <p className="text-4xl font-bold text-white mb-2">{t('brightlocal.onePlatform')}</p>
              <p className="text-white/60 text-sm mb-6">{t('brightlocal.onePlatformSub')}</p>
              <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }} data-testid="cta-pricing">
                {t('brightlocal.getStarted')} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">{t('brightlocal.readySwitch')}</h2>
            <p className="text-slate-400 mb-8">{t('brightlocal.readySwitchSub')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/signup" className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }}>{t('brightlocal.startTrial')}</Link>
              <Link to="/compare/vendasta" className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105 border border-white/20 text-white hover:bg-white/10">vs Vendasta</Link>
            </div>
          </div>
        </section>
        <SiteFooter />
      </div>
    </>
  );
}
